import { RECAPTCHA_SITE_KEY } from '../../../constants/constants';
import { IPersonalData } from '../../../interfaces/auth';
import { ICustomResponse } from '../../../interfaces/businessData';
import {
  IIntercomTokenResponse,
  IUserCheckEmailResponse,
  IUserDataResponse,
  IUserImageResponse,
  IUserVerificationResponse
} from '../../../interfaces/responseInterface';
import {
  CHECK_EMAIL_MOCK_RESPONSE,
  INTERCOM_TOKEN_MOCK_RESPONSE,
  USER_ATTRIBUTES_MOCK_RESPONSE,
  USER_IMAGE_MOCK_RESPONSE
} from '../../../mocks/users';
import { HttpService, HttpServiceServer } from '../../http/httpService';

export class Users {
  static async getUserAttributes(): Promise<IUserDataResponse> {
    const response = await HttpServiceServer.get<IUserDataResponse>('users', {}, USER_ATTRIBUTES_MOCK_RESPONSE);
    return response.data;
  }

  static async getIntercomToken(): Promise<string | null> {
    const response = await HttpServiceServer.get<IIntercomTokenResponse>(
      'users/intercom-token',
      {},
      INTERCOM_TOKEN_MOCK_RESPONSE
    );
    return response.data.token;
  }

  static async patchUserAttributes(userEmail: string, user: IPersonalData): Promise<void> {
    await HttpService.patch<IUserDataResponse>(`users/${userEmail}`, user, {}, {});
  }

  static async putUserImage(userEmail: string, base64: string): Promise<IUserImageResponse> {
    const response = await HttpService.put<IUserImageResponse>(
      `users/${userEmail}/profile-image`,
      { image: base64 },
      {},
      USER_IMAGE_MOCK_RESPONSE
    );
    return response.data;
  }

  static async deleteUserImage(userEmail: string): Promise<IUserImageResponse> {
    const response = await HttpService.delete<IUserImageResponse>(
      `users/${userEmail}/profile-image`,
      {},
      USER_IMAGE_MOCK_RESPONSE
    );
    return response.data;
  }

  static async notifyLogin(provider: string): Promise<void> {
    await HttpServiceServer.post<ICustomResponse>('users/notify-login', { provider }, {}, {});
  }

  static async signUp(email: string, password: string, token: string): Promise<void> {
    const recaptcha = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'signup' });
    await HttpServiceServer.post<ICustomResponse>(
      'users/signup',
      { email, password, verificationToken: token },
      { headers: { 'x-recaptcha': recaptcha } },
      {}
    );
  }

  static async signUpMicrosoft(
    email: string,
    password: string,
    provider: string,
    token: string,
    verificationToken?: string
  ): Promise<void> {
    const recaptcha = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'signup' });
    await HttpServiceServer.post<ICustomResponse>(
      'users/signup',
      { email, password, provider, token, verificationToken },
      { headers: { 'x-recaptcha': recaptcha } },
      {}
    );
  }

  static async verifyEmail(email: string, code: string): Promise<IUserVerificationResponse> {
    const recaptcha = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'verify_email' });
    const headers = { 'x-recaptcha': recaptcha };
    const response = await HttpServiceServer.post<IUserVerificationResponse>(
      'users/verify-email',
      { email, code },
      { headers },
      {}
    );
    return response.data;
  }

  static async resendCode(email: string): Promise<void> {
    const recaptcha = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'resend_code' });
    const headers = { 'x-recaptcha': recaptcha };
    await HttpServiceServer.post<void>('users/resend-verification-email', { email }, { headers }, {});
  }

  static async checkEmail(email: string): Promise<IUserCheckEmailResponse> {
    const recaptcha = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'check_email' });
    const headers = { 'x-recaptcha': recaptcha };
    const response = await HttpServiceServer.get<IUserCheckEmailResponse>(
      'users/check-email',
      { params: { email: email }, headers },
      CHECK_EMAIL_MOCK_RESPONSE
    );
    return response.data;
  }
}

export default Users;
